import React, { useState } from 'react'
import { Camera, Video, FileText, Download, Share2, Grid, Edit3, Pencil, FolderUp, Cctv, Printer, Mic, Scan } from 'lucide-react'
import ImageCard from '../ImageCard'
import VideoCard from '../VideoCard'
import OtherFileList from '../OtherFileList'
import ImageCarousel from '../ImageCarousel'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable'
import {
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import MessageDialog from '../../../shared/ui/MessageDialog'

// Movie Player Component using centralized blob management
const MoviePlayer = ({ movie, isSelected, isSelectionMode, onToggleItemSelection, service, serviceParams = [], mode = 'edit' }) => {
  const handleCheckboxChange = (e) => {
    e.stopPropagation()
    onToggleItemSelection(movie.objectMovieId)
  }

  return (
    <div className="relative">
      {/* Selection checkbox */}
      {isSelectionMode && (
        <div className="absolute top-2 left-2 z-10">
          <input
            type="checkbox"
            data-testid="movie-selection-checkbox"
            checked={isSelected}
            onChange={handleCheckboxChange}
            className="w-4 h-4 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500"
          />
        </div>
      )}

      {/* Video player using centralized blob management */}
      <VideoCard
        video={movie}
        pageType={mode === 'share' ? 'share' : 'satsueiDataToroku'}
        imageService={service}
        serviceParams={serviceParams}
        showControls={true}
        autoPlay={false}
        muted={true}
      />
    </div>
  )
}

// Sortable Image Item Component using @dnd-kit
const SortableImageItem = ({ image, isSelected, isSelectionMode, onToggleItemSelection, onImageEdit, onImageClick, service, serviceParams = [], pageType = 'satsueiDataToroku', showImageEdit = true }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: image.objectImageId })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const handleImageClick = (e) => {
    // Don't open carousel if clicking on tag button or edit button
    if (e.target.closest('button') || e.target.closest('input')) {
      return
    }
    e.stopPropagation()
    
    // Small delay to ensure this is a click, not a drag
    setTimeout(() => {
      onImageClick(image)
    }, 10)
  }

  const handleEditClick = (e) => {
    e.stopPropagation()
    onImageEdit(image)
  }

  const handleCheckboxChange = (e) => {
    e.stopPropagation()
    onToggleItemSelection(image.objectImageId)
  }

  return (
    <div 
      ref={setNodeRef}
      style={style}
      className="relative flex justify-center"
      {...attributes}
      onClick={handleImageClick}
    >
      <div className="relative">
        {/* ImageCard with drag listeners applied only to the image area, not buttons */}
        <div className="cursor-pointer">
          <ImageCard 
            image={image} 
            dragListeners={listeners}
            imageService={service}
            serviceParams={serviceParams}
            pageType={pageType}
          />
        </div>
        
        {/* Selection checkbox - not draggable */}
        {isSelectionMode && (
          <div className="absolute top-2 left-2 z-10">
            <input
              type="checkbox"
              data-testid="image-selection-checkbox"
              checked={isSelected}
              onChange={handleCheckboxChange}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
            />
          </div>
        )}

        {/* Edit button - not draggable */}
        {!isSelectionMode && showImageEdit && (
          <button
            onClick={handleEditClick}
            className="absolute top-2 right-2 z-10 p-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            title="画像編集"
          >
            <Edit3 size={12} />
          </button>
        )}
      </div>
    </div>
  )
}

const SatsueiDataMediaArea = ({
  // Mode configuration
  mode = 'edit', // 'edit' | 'share' | 'view'
  
  // Service configuration
  service,
  serviceParams = [],
  
  // Media props
  selectedMediaType,
  onMediaTypeChange,
  imageList,
  movieList,
  otherFileList,
  imageFolderList,
  selectedImageFolderId,
  onImageFolderChange,
  
  // Selection props (only for edit mode)
  isSelectionMode,
  selectedItems,
  onToggleSelectionMode,
  onToggleItemSelection,
  onSelectAll,
  onClearAll,
  onSelectionAction,
  
  // Action props
  onImageEdit,
  onDownload,
  onShare,
  onComposite,
  onAnnotation,
  onPixClient,
  onScanSnap,
  onCamera,
  onVoiceRecognition,
  onOCR,
  isDownloading = false,
  cameraDisabled = false,
  pixClientDisabled = false,
  voiceRecognitionDisabled = false,
  ocrDisabled = false,

  // User authority for extra features (0/1). If provided, button is shown only when === 1.
  userPixCameraUsage,
  userPixClientUsage,
  userVoiceRecognitionUsage,
  userOcrUsage,

  // Drag and drop props (only for edit mode)
  getDropZoneProps,
  isDragOver,

  // Other props
  objectId,
  onImageReorder,
  onOtherFileReorder
}) => {
  const [showImageCarousel, setShowImageCarousel] = useState(false)
  const [carouselInitialIndex, setCarouselInitialIndex] = useState(0)
  const [printMessageModal, setPrintMessageModal] = useState({ open: false, message: '' })
  
  // Determine if features are enabled based on mode
  const isReadOnly = mode === 'share' || mode === 'view'
  const showSelectionMode = !isReadOnly && isSelectionMode !== undefined
  const showDragAndDrop = !isReadOnly && getDropZoneProps !== undefined
  const showImageEdit = !isReadOnly && onImageEdit !== undefined
  const showShare = !isReadOnly && onShare !== undefined
  const showComposite = !isReadOnly && onComposite !== undefined
  const showAnnotation = !isReadOnly && onAnnotation !== undefined
  const showFolderSelection = !isReadOnly && imageFolderList && imageFolderList.length > 0
  const showPixClient = !isReadOnly && onPixClient !== undefined && (userPixClientUsage === undefined || userPixClientUsage === 1)
  const showScanSnap = !isReadOnly && onScanSnap !== undefined && selectedMediaType === 'image'
  const showCamera = !isReadOnly && onCamera !== undefined && (userPixCameraUsage === undefined || userPixCameraUsage === 1)
  const showVoiceRecognition = !isReadOnly && onVoiceRecognition !== undefined && (userVoiceRecognitionUsage === undefined || userVoiceRecognitionUsage === 1)
  const showOCR = !isReadOnly && onOCR !== undefined && (userOcrUsage === undefined || userOcrUsage === 1)

  // @dnd-kit sensors with activation constraint to prevent accidental drags
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px movement before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const getCurrentMediaList = () => {
    switch (selectedMediaType) {
      case 'image':
        return imageList || []
      case 'movie':
        return movieList || []
      case 'other':
        return otherFileList || []
      default:
        return []
    }
  }

  const handleDragEnd = (event) => {
    if (isReadOnly) return
    
    const { active, over } = event

    if (active.id !== over?.id && over) {
      if (selectedMediaType === 'other') {
        // Handle other file reordering
        const oldIndex = (otherFileList || []).findIndex(item => `other-${item.objectOtherFileId}` === active.id)
        const newIndex = (otherFileList || []).findIndex(item => `other-${item.objectOtherFileId}` === over.id)

        if (oldIndex !== -1 && newIndex !== -1) {
          const newOtherFileList = arrayMove(otherFileList || [], oldIndex, newIndex)
          onOtherFileReorder && onOtherFileReorder(newOtherFileList)
        }
      } else {
        // Handle image reordering
        const oldIndex = (imageList || []).findIndex(item => item.objectImageId === active.id)
        const newIndex = (imageList || []).findIndex(item => item.objectImageId === over.id)

        if (oldIndex !== -1 && newIndex !== -1) {
          const newImageList = arrayMove(imageList || [], oldIndex, newIndex)
          onImageReorder && onImageReorder(newImageList)
        }
      }
    }
  }

  const handleImageClick = (clickedImage) => {
    // Find the index of the clicked image in the imageList
    const clickedIndex = imageList?.findIndex(img => img.objectImageId === clickedImage.objectImageId) ?? 0
    setCarouselInitialIndex(clickedIndex >= 0 ? clickedIndex : 0)
    setShowImageCarousel(true)
  }

  const handlePrintSelected = async () => {
    if (selectedMediaType !== 'image' || !objectId || !imageList?.length || !service?.getImageAbsoluteUrl) return
    const count = selectedItems?.length ?? 0
    if (count !== 1) {
      setPrintMessageModal({
        open: true,
        message: count === 0 ? '印刷するには1枚選択してください' : '印刷は1枚だけ選択してください'
      })
      return
    }
    const singleImage = imageList.find((img) => selectedItems.includes(img.objectImageId))
    if (!singleImage) return
    try {
      const url = await service.getImageAbsoluteUrl(objectId, singleImage)
      if (!url) return
      const pixUrl = `pixclient://print/?url=${encodeURIComponent(url)}`
      try {
        const iframe = document.createElement('iframe')
        iframe.style.display = 'none'
        iframe.src = pixUrl
        document.body.appendChild(iframe)
        setTimeout(() => document.body.removeChild(iframe), 500)
      } catch (e) {
        window.location.href = pixUrl
      }
    } catch (err) {
      console.error('Failed to get presigned URL for print:', err)
    }
  }

  const printAllowed = selectedMediaType === 'image' && !!service?.getImageAbsoluteUrl && !!objectId

  const getItemId = (item) => {
    switch (selectedMediaType) {
      case 'image':
        return item.objectImageId
      case 'movie':
        return item.objectMovieId
      case 'other':
        return item.objectOtherFileId
      default:
        return null
    }
  }

  const renderMediaItem = (item) => {
    const itemId = getItemId(item)
    const isSelected = selectedItems ? selectedItems.includes(itemId) : false

    switch (selectedMediaType) {
      case 'image':
        return (
          <SortableImageItem
            key={itemId}
            image={item}
            isSelected={isSelected}
            isSelectionMode={showSelectionMode && isSelectionMode}
            onToggleItemSelection={onToggleItemSelection}
            onImageEdit={onImageEdit}
            onImageClick={handleImageClick}
            service={service}
            serviceParams={serviceParams}
            pageType={mode === 'share' ? 'share' : 'satsueiDataToroku'}
            showImageEdit={showImageEdit}
          />
        )

      case 'movie':
        return (
          <MoviePlayer
            key={itemId}
            movie={item}
            isSelected={isSelected}
            isSelectionMode={showSelectionMode && isSelectionMode}
            onToggleItemSelection={onToggleItemSelection}
            service={service}
            serviceParams={serviceParams}
            mode={mode}
          />
        )

      case 'other':
        return (
          <OtherFileList
            key={itemId}
            file={item}
            isSelected={isSelected}
            isSelectionMode={showSelectionMode && isSelectionMode}
            onToggleItemSelection={onToggleItemSelection}
            imageService={service}
            serviceParams={serviceParams}
            pageType={mode === 'share' ? 'share' : 'satsueiDataToroku'}
          />
        )

      default:
        return null
    }
  }

  return (
    <div 
      data-testid="satsuei-data-media-area"
      className="bg-white rounded-lg shadow-sm border border-gray-200 lg:sticky lg:top-4 lg:h-[calc(100vh-15rem)] flex flex-col overflow-hidden"
    >
      {/* Media Type Selection */}
      <div className="p-3 border-b border-gray-200">
        <div className="flex items-center space-x-2">
          <button
            data-testid="image-tab"
            onClick={() => onMediaTypeChange('image')}
            className={`flex items-center space-x-1 px-3 py-1.5 rounded text-sm transition-colors ${
              selectedMediaType === 'image' 
                ? 'bg-red-600 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            <Camera size={14} />
            <span>画像</span>
          </button>

          <button
            data-testid="movie-tab"
            onClick={() => onMediaTypeChange('movie')}
            className={`flex items-center space-x-1 px-3 py-1.5 rounded text-sm transition-colors ${
              selectedMediaType === 'movie'
                ? 'bg-red-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            <Video size={14} />
            <span>動画</span>
          </button>

          {/* Only show "other" tab in edit mode */}
          {!isReadOnly && (
            <button
              data-testid="other-tab"
              onClick={() => onMediaTypeChange('other')}
              className={`flex items-center space-x-1 px-3 py-1.5 rounded text-sm transition-colors ${
                selectedMediaType === 'other' 
                  ? 'bg-red-600 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              <FileText size={14} />
              <span>その他</span>
            </button>
          )}
        </div>
      </div>

      {/* Folder Selection (for images) - only in edit mode */}
      {showFolderSelection && selectedMediaType === 'image' && imageFolderList.filter(folder => folder.imageFolderId !== 0).length > 0 && (
        <div className="p-3 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <label htmlFor="folder-select" className="text-sm font-medium text-gray-700">保存フォルダー:</label>
            <select
              id="folder-select"
              value={selectedImageFolderId}
              onChange={(e) => onImageFolderChange(parseInt(e.target.value))}
              className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value={99}>
                全フォルダ表示 ({imageFolderList.reduce((sum, folder) => sum + parseInt(folder.imageCount), 0)})
              </option>
              {imageFolderList.map((folder) => (
                <option key={folder.imageFolderId} value={folder.imageFolderId}>
                  {folder.imageFolderName} ({folder.imageCount})
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="p-3 border-b border-gray-200">
        <div className="flex items-center justify-between min-h-[28px]">
          {/* Left side - Selection controls (only in edit mode) */}
          <div className="flex items-center space-x-1">
            {showSelectionMode && (
              <>
                <button
                  onClick={onToggleSelectionMode}
                  aria-label={isSelectionMode ? '選択モードを終了' : '選択モードを開始'}
                  className={`px-2 py-1 text-xs rounded transition-colors ${
                    isSelectionMode 
                      ? 'bg-red-600 text-white' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {isSelectionMode ? '選択終了' : '選択モード'}
                </button>

                {isSelectionMode && (
                  <>
                    <button
                      onClick={onSelectAll}
                      aria-label="すべてのアイテムを選択"
                      className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                    >
                      全選択
                    </button>
                    <button
                      onClick={onClearAll}
                      aria-label="すべての選択を解除"
                      className="px-2 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                    >
                      全解除
                    </button>
                    <button
                      onClick={() => onSelectionAction('delete')}
                      aria-label="選択したアイテムを削除"
                      className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                    >
                      削除
                    </button>
                    {printAllowed && (
                      <button
                        onClick={handlePrintSelected}
                        aria-label="選択した画像をPIX Clientで印刷"
                        className="px-2 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors flex items-center space-x-1"
                        title="選択した画像をPIX Clientで印刷"
                        data-testid="selection-print-button"
                      >
                        <Printer size={12} />
                        <span>印刷</span>
                      </button>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          {/* Right side - Action buttons */}
          {!isSelectionMode && (
            <div className="flex items-center space-x-1">
              {showCamera && selectedMediaType === 'image' && (
                <button
                  id="camera-btn"
                  onClick={onCamera}
                  disabled={cameraDisabled}
                  aria-label="カメラ"
                  className={`p-1.5 rounded transition-colors ${
                    cameraDisabled
                      ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                      : 'bg-teal-600 text-white hover:bg-teal-700'
                  }`}
                  title="カメラ"
                  data-testid="camera-button"
                >
                  <Cctv size={14} />
                </button>
              )}
              {showPixClient && selectedMediaType === 'image' && (
                <button
                  id="pixclient-btn"
                  onClick={onPixClient}
                  disabled={pixClientDisabled}
                  aria-label="Pix Client"
                  className={`p-1.5 rounded transition-colors ${
                    pixClientDisabled
                      ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                      : 'bg-indigo-600 text-white hover:bg-indigo-700'
                  }`}
                  title="Pix Client"
                  data-testid="pixclient-button"
                >
                  <FolderUp size={14} />
                </button>
              )}
              {showScanSnap && selectedMediaType === 'image' && (
                <button
                  id="scansnap-btn"
                  onClick={onScanSnap}
                  aria-label="スキャナー"
                  className="p-1.5 bg-cyan-600 text-white rounded hover:bg-cyan-700 transition-colors"
                  title="スキャナー"
                  data-testid="scansnap-button"
                >
                  <Printer size={14} />
                </button>
              )}
              {showVoiceRecognition && (
                <button
                  id="voice-btn"
                  onClick={onVoiceRecognition}
                  disabled={voiceRecognitionDisabled}
                  aria-label="音声入力"
                  className={`p-1.5 rounded transition-colors ${
                    voiceRecognitionDisabled
                      ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                      : 'bg-rose-600 text-white hover:bg-rose-700'
                  }`}
                  title="音声入力"
                  data-testid="voice-button"
                >
                  <Mic size={14} />
                </button>
              )}
              {showOCR && selectedMediaType === 'image' && (
                <button
                  id="ocr-btn"
                  onClick={onOCR}
                  disabled={ocrDisabled}
                  aria-label="OCR入力"
                  className={`p-1.5 rounded transition-colors ${
                    ocrDisabled
                      ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                      : 'bg-indigo-600 text-white hover:bg-indigo-700'
                  }`}
                  title="OCR入力"
                  data-testid="ocr-button"
                >
                  <Scan size={14} />
                </button>
              )}
              {showAnnotation && selectedMediaType === 'image' && (
                <button
                  id="annotation-btn"
                  onClick={onAnnotation}
                  aria-label="画像手書き編集"
                  className="p-1.5 bg-orange-600 text-white rounded hover:bg-orange-700 transition-colors"
                  title="画像手書き編集"
                  data-testid="annotation-button"
                >
                  <Pencil size={14} />
                </button>
              )}
              {showComposite && selectedMediaType === 'image' && (
                <button
                  id="composite-btn"
                  onClick={onComposite}
                  aria-label="画像4分割合成"
                  className="p-1.5 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors"
                  title="画像4分割合成"
                >
                  <Grid size={14} />
                </button>
              )}
              <button
                id="download-btn"
                data-testid="download-button"
                onClick={onDownload}
                disabled={isDownloading}
                aria-label={isDownloading ? "ダウンロード中..." : "ダウンロード"}
                className={`p-1.5 rounded transition-colors ${
                  isDownloading 
                    ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                    : 'bg-gray-600 text-white hover:bg-gray-700'
                }`}
                title={isDownloading ? "ダウンロード中..." : "ダウンロード"}
              >
                <Download size={14} />
              </button>
              {showShare && (
                <button
                  id="share-btn"
                  data-testid="share-button"
                  onClick={onShare}
                  aria-label="シェア"
                  className="p-1.5 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                  title="シェア"
                >
                  <Share2 size={14} />
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Media Display Area */}
      <div
        data-testid="media-display-area"
        {...(showDragAndDrop ? getDropZoneProps() : {})}
        className={`p-3 pb-3 min-h-80 relative flex-1 overflow-y-auto ${
          showDragAndDrop && isDragOver 
            ? 'bg-blue-50 border-2 border-dashed border-blue-400' 
            : 'bg-gray-50'
        }`}
      >
        {showDragAndDrop && isDragOver && (
          <div className="absolute inset-0 z-20 pointer-events-none flex items-center justify-center bg-blue-50/90 border-2 border-dashed border-blue-400 text-blue-600 text-sm font-medium rounded">
            このエリアにファイルをドロップしてください
          </div>
        )}

        <>
            {(selectedMediaType === 'image' || selectedMediaType === 'other') && !isReadOnly ? (
              <>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                <SortableContext
                  items={selectedMediaType === 'other' 
                    ? (otherFileList || []).map(item => `other-${item.objectOtherFileId}`)
                    : (imageList || []).map(item => item.objectImageId)
                  }
                >
                  <div 
                    className={selectedMediaType === 'other' 
                      ? "flex flex-col" 
                      : selectedMediaType === 'movie'
                      ? "flex flex-col gap-3 justify-start"
                      : "grid gap-3 justify-items-center"
                    }
                    style={selectedMediaType === 'image' ? { gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))' } : undefined}
                  >
                    {getCurrentMediaList().map((item, index) => renderMediaItem(item, index))}
                  </div>
                </SortableContext>
                </DndContext>
              </>
            ) : (
              <div 
                className={selectedMediaType === 'other' 
                  ? "flex flex-col" 
                  : selectedMediaType === 'movie'
                  ? "flex flex-col gap-3 justify-start"
                  : "grid gap-3 justify-items-center"
                }
                style={selectedMediaType === 'image' ? { gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))' } : undefined}
              >
                {getCurrentMediaList().map((item, index) => renderMediaItem(item, index))}
              </div>
            )}
          </>

        {(!showDragAndDrop || !isDragOver) && getCurrentMediaList().length === 0 && (
          <div className="flex items-center justify-center h-48 text-gray-500 text-sm">
            {selectedMediaType === 'image' && '画像がありません'}
            {selectedMediaType === 'movie' && '動画がありません'}
            {selectedMediaType === 'other' && 'ファイルがありません'}
            {!['image', 'movie', 'other'].includes(selectedMediaType) && 'メディアがありません'}
          </div>
        )}

        {/* Print rule message modal */}
        <MessageDialog
          isOpen={printMessageModal.open}
          onClose={() => setPrintMessageModal((p) => ({ ...p, open: false }))}
          title="印刷"
          message={printMessageModal.message}
          buttonText="OK"
          type="warning"
        />

        {/* Image Carousel Modal - Inside Media Area */}
        {selectedMediaType === 'image' && (
          <ImageCarousel
            images={imageList}
            isOpen={showImageCarousel}
            onClose={() => setShowImageCarousel(false)}
            objectId={objectId}
            sharePixId={mode === 'share' ? serviceParams[1] : undefined}
            showTags={mode === 'edit'}
            imageService={service}
            serviceParams={serviceParams}
            initialIndex={carouselInitialIndex}
          />
        )}
      </div>
    </div>
  )
}

export default SatsueiDataMediaArea
